<template>
    <div class="rounded-t overflow-hidden border border-b-0">
        <table class="table table-with-links w-full">
            <thead>
                <tr>
                    <th v-if="!isOnlyPicker && !loadShippedAndInvoiced" class="text-left">Loaded</th>
                    <th v-if="load_type == 'delivery'" class="text-left whitespace-nowrap">Visit #</th>
                    <th class="text-left whitespace-nowrap">Order #</th>
                    <th class="text-left">Customer</th>
                    <th class="text-left">Status</th>
                    <th class="hidden md:table-cell text-right">Subtotal</th>
                    <th v-if="isPicker" class="text-right whitespace-nowrap">Next Zone</th>
                    <th class="w-min text-left" width="1">Packaging</th>
                    <th class="w-min text-left"></th>
                    <th class="w-min text-left"></th>
                </tr>
            </thead>
                <tr
                    v-for="(loadOrder, index) in filteredLoadOrders"
                    :key="loadOrder.id"
                    class="text-xs lg:text-base"
                    :class="{
                        'order_picking_checked': loadOrder.order_id != null && loadOrder.order.picking_checked_at != null && loadOrder.order.packaged_at == null && loadOrder.order.loaded_at == null,
                        'order_packaged': loadOrder.order_id != null && loadOrder.order.packaged_at != null && loadOrder.order.loaded_at == null,
                        'order_loaded': (loadOrder.order_id != null && loadOrder.order.loaded_at != null)
                            || (loadOrder.order_id == null && loadOrder.loaded_at != null),
                    }"
                    :style="{
                        'background': getBackground(loadOrder, index)
                    }"
                >
                    <template v-if="loadOrder.order_id == null">
                        <td v-if="!isOnlyPicker && !loadShippedAndInvoiced" class="!py-1 text-center" width="56px;">
                            <input
                                v-if="load_loader_id"
                                type="checkbox"
                                class="rounded h-6 w-6"
                                :checked="!!loadOrder.loaded_at"
                                @change="onNonOrderLoadedChange(loadOrder, $event)"
                            />
                        </td>
                        <td v-if="load_type == 'delivery'" class="!py-1 text-left">
                            <span class="block px-2 py-1">
                                {{ loadOrder.delivery_order_index + 1 }}
                            </span>
                        </td>
                        <td :colspan="nonOrderDescriptionColspan" class="!py-1 text-left">
                            <span class="block px-2 py-1">
                                {{ loadOrder.description }}
                            </span>
                        </td>
                    </template>
                    <template v-else>
                        <td v-if="!isOnlyPicker && !loadShippedAndInvoiced" class="!py-1 text-center" width="56px;">
                            <!-- Temporarily commented until Kurtis wants picking to be required. Replaces the line below - we don't need two. -->
                            <!-- <input v-if="load_loader_id && loadOrder.order.picked_at && loadOrder.order.picking_checked_at && (loadableLoadOrderIds.includes(loadOrder.id) || loadOrder.order.loaded)" type="checkbox" class="rounded h-6 w-6" name="loaded" v-model="loadOrder.order.loaded" @input="autoSaveOrderLoading(loadOrder.id)"/> -->
                            <input v-if="load_loader_id 
                                && (loadOrder.order.picked_at != null 
                                || loadOrder.order.is_wholesale) 
                                && loadOrder.order.picking_checked_at 
                                && (loadableLoadOrderIds.includes(loadOrder.id) || loadOrder.order.loaded)
                                && (!loadOrder.order.is_wholesale || !orderHasMilkCrateItems(loadOrder.order) || (loadOrder.order.milk_crates !== null && loadOrder.order.milk_crates >= 0))" 
                                type="checkbox" class="rounded h-6 w-6" name="loaded" v-model="loadOrder.order.loaded"
                                @input="autoSaveOrderLoading(loadOrder.id)"/>
                        </td>
                        <td v-if="load_type == 'delivery'" class="!py-1 text-left">
                            <Link class="block px-2 py-1" :href="orderUrl(loadOrder.order)">
                                {{ loadOrder.delivery_order_index + 1 }}
                            </Link>
                        </td>
                        <td class="!py-1 text-left">
                            <Link class="block px-2 py-1" :href="orderUrl(loadOrder.order)">
                                {{ loadOrder.order.order_number }}
                            </Link>
                        </td>
                        <td class="!py-1 text-left">
                            <Link class="block px-2 py-1" :href="orderUrl(loadOrder.order)">
                                {{ loadOrder.order.customer?.name || loadOrder.order.shipping_first_name + ' ' + loadOrder.order.shipping_last_name }}
                            </Link>
                        </td>
                        <td class="!py-1 text-left">
                            <Link class="block px-2 py-1" :href="orderUrl(loadOrder.order)">
                                {{ loadOrder.order.statusDef }}
                            </Link>
                        </td>
                        <td class="!py-1 hidden md:table-cell text-right">
                            <Link class="block px-1 md:px-2 py-1" :href="orderUrl(loadOrder.order)">
                                {{ toCurrency(subtotalBeforeDiscount(loadOrder.order)) }}
                            </Link>
                        </td>
                        <td v-if="isPicker" class="!py-1 text-right">
                            <Link class="block px-2 py-1" :href="orderUrl(loadOrder.order)">
                                <div v-if="loadOrder.order.picked_at == null && loadOrder.order.picking_checked_at == null">
                                    {{ loadOrder.order.next_picking_zone }}
                                </div>
                                <img v-else src="/images/icons/check.svg" width="24" height="24" class="ml-auto" />
                            </Link>
                        </td>
                        <td class="!py-1 text-center" width="1">
                            <div class="flex gap-x-1 no-wrap">
                                <!-- Box Count -->
                                <InputGroup v-if="loadOrder.order.picking_checked_at || loadOrder.order.packaged_at">
                                    <InputGroupText position="prepend">
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'box']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Boxes</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-8 px-0.5 rounded-l-none text-center text-xs lg:text-base" name="box_count" v-model="loadOrder.order.box_count" @keydown.enter.prevent @input="autoSaveOrderPackaging(loadOrder.id)"/>
                                </InputGroup>
                                <!-- Pallet Count -->
                                <InputGroup v-if="loadOrder.order.is_wholesale && (loadOrder.order.picking_checked_at || loadOrder.order.packaged_at)">
                                    <InputGroupText position="prepend"> 
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'pallet']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Pallets</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-8 px-0.5 rounded-l-none text-center text-xs lg:text-base" name="pallet_count" v-model="loadOrder.order.pallet_count" @keydown.enter.prevent @input="autoSaveOrderPackaging(loadOrder.id)"/>
                                </InputGroup>
                                <!-- Milk Crate Count -->
                                <InputGroup v-if="loadOrder.order.is_wholesale && (loadOrder.order.picking_checked_at || loadOrder.order.packaged_at) && orderHasMilkCrateItems(loadOrder.order)">
                                    <InputGroupText position="prepend">
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fa-solid', 'fa-table-cells']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Milk Crates</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-8 px-0.5 rounded-l-none text-center text-xs lg:text-base" name="milk_crate_count" v-model="loadOrder.order.milk_crates" @keydown.enter.prevent @input="autoSaveOrderMilkCrates(loadOrder.id)"
                                    />
                                </InputGroup>
                                <!-- Tote Count -->
                                <InputGroup v-if="!loadOrder.order.is_wholesale && loadOrder.order.shipping_method == 'delivery' && (loadOrder.order.picking_checked_at || loadOrder.order.packaged_at)">
                                    <InputGroupText position="prepend">
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'snowflake']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Totes</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-8 px-0.5 rounded-l-none text-center text-xs lg:text-base" name="tote_count" v-model="loadOrder.order.tote_count" @keydown.enter.prevent @input="autoSaveOrderToteCount(loadOrder.id)"/>
                                </InputGroup>
                            </div>
                        </td>
                        <td class="!py-1" style="width: 56px;">
                            <jet-secondary-button v-if="loadOrder.order.box_count > 0 && (!loadOrder.order.is_wholesale || !orderHasMilkCrateItems(loadOrder.order) || (loadOrder.order.milk_crates !== null && loadOrder.order.milk_crates >= 0))" type="button" class="leading-none py-1 px-2 ml-1 mr-2 align-middle" @click="printLabels(loadOrder.order)">
                                <img src="/images/icons/print_w.svg" width="16" height="16" style="min-width: 16px;" alt="Print Labels">
                            </jet-secondary-button>
                        </td>
                        <td class="!py-1 text-right whitespace-nowrap">
                            <jet-button
                                v-if="isPicker && loadOrder.order.picked_at === null && loadOrder.order.picking_checked_at === null"
                                class="mr-1"
                                @click="pickOrder(loadOrder.order)"
                            >
                                Pick
                            </jet-button>
                            <!-- Temporarily commented until Kurtis wants picking to be required. Replaces the other "Check" button - we don't need two. -->
                            <!-- <jet-button
                                v-if="loadOrder.order.picked_at != null && loadOrder.order.picking_checked_at === null && !isOnlyPicker"    
                                @click="$inertia.visit(`/orders/${loadOrder.order.id}/checking`)"
                            >
                                Check
                            </jet-button> -->
                            <jet-button
                                class="mr-1"
                                v-if="(loadOrder.order.picked_at != null || loadOrder.order.is_wholesale)
                                    && loadOrder.order.picking_checked_at === null && !isOnlyPicker"
                                @click="$inertia.visit(`/orders/${loadOrder.order.id}/checking`)"
                            >
                                Check
                            </jet-button>
                        </td>
                    </template>
                </tr>
        </table>
    </div>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import { DateTime } from "luxon";
import JetInput from '@/Jetstream/Input';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetButton from '@/Jetstream/Button';
import InputGroup from '@/Jetstream/InputGroup';
import InputGroupText from '@/Jetstream/InputGroupText';
import BetterTooltip from '@/Jetstream/BetterTooltip';
import { computeOrderTotals } from '@/Composables/useOrderTotals';

export default {
    setup () {
        const { isPicker, isOnlyPicker } = UseUserAuth(usePage().props.value.user);

        return { isPicker, isOnlyPicker }
    },

    components: {
        Link,
        JetInput,
        JetSecondaryButton,
        JetButton,
        InputGroup,
        InputGroupText,
        BetterTooltip
    },

    props: ['orderType', 'loadOrders', 'load_id', 'load_loader_id', 'load_type', 'loadShippedAndInvoiced'],

    data () {
        return {
            interval: null,

            saveOrderPackagingTimeout: {},
            saveOrderToteCountTimeout: {},
            saveOrderLoadingTimeout: {},
            saveOrderMilkCratesTimeout: {},
            saveOrderNonOrderLoadingTimeout: {},
            loadOrdersData: this.loadOrders
        };
    },

    created () {
        this.interval = setInterval(() => {
            this.updatePickingLoadingData();
        }, 10 * 1000)
    },

    beforeUnmount() {
        clearInterval(this.interval);
    },

    methods: {
        orderUrl (order) {
            if (order == null) {
                return '';
            } else if (this.isOnlyPicker) {
                return route('orders.pick', {
                    order: order.id,
                    return: route('loads.show', this.load_id),
                })
            } else {
                return route('orders.show', {
                    order: order.id,
                    return: route('loads.show', this.load_id),
                })
            }
        },

        pickOrder (order) {
            if (order.is_wholesale) {
                this.$inertia.visit(`/orders/${order.id}/picking`);
            } else {
                const query = `orders[]=${order.id}`;

                this.$inertia.visit(`/multiPicking?${query}`);
            }
        },

        autoSaveOrderPackaging (load_order_id) {
            this.saveOrderDebounce('Packaging', load_order_id, () => {
                let load_order = this.loadOrdersData.find(load_order => load_order.id == load_order_id);

                axios.post(route('orders.update_packaging_counts', {order: load_order.order.id, box_count: load_order.order.box_count, pallet_count: load_order.order.pallet_count}))
                .then(() => {
                    axios.get(route('loads.load_picking_data', {load: this.load_id}))
                    .then((response) => {
                        this.$emit('updatePickingData', response.data);
                    })
                });

            });
        },

        autoSaveOrderMilkCrates(load_order_id) {
            this.saveOrderDebounce('MilkCrates', load_order_id, () => {
                let load_order = this.loadOrdersData.find(lo => lo.id == load_order_id);
                load_order.order.updated_at = DateTime.now();

                axios.post(route('orders.update_milk_crates', {order: load_order.order.id, milk_crates: load_order.order.milk_crates}))
                .then(() => {
                    axios.get(route('loads.load_picking_data', { load: this.load_id }))
                    .then((response) => {
                        this.$emit('updatePickingData', response.data);
                    })
                });
            });
        },

        orderHasMilkCrateItems(order) {
            return order.order_items.some(item => item.product && item.product.milk_crate);
        },

        autoSaveOrderToteCount (load_order_id) {
            this.saveOrderDebounce('ToteCount', load_order_id, () => {
                let load_order = this.loadOrdersData.find(load_order => load_order.id == load_order_id);
                
                load_order.order.updated_at = DateTime.now();

                axios.post(route('orders.update_tote_count', {order: load_order.order.id, tote_count: load_order.order.tote_count}));
            });
        },

        autoSaveOrderLoading (load_order_id) {
            this.saveOrderDebounce('Loading', load_order_id, () => {
                let load_order = this.loadOrdersData.find(load_order => load_order.id == load_order_id);
                
                if (load_order.order.loaded) load_order.order.loaded_at = DateTime.now();
                else load_order.order.loaded_at = null;
                load_order.order.updated_at = DateTime.now();

                axios.post(route('orders.update_loaded_at', { order: load_order.order.id }), {
                    loaded: load_order.order.loaded,
                    load_id: this.load_id,
                })
                    .catch(() => {
                        this.updatePickingLoadingData();
                    });
            });
        },

        // handles non-order load stops (uses loaded-at column in load_orders, rather than loaded_at column in orders)
        onNonOrderLoadedChange (loadOrder, event) {
            const loaded = event.target.checked;
            this.saveOrderDebounce('NonOrderLoading', loadOrder.id, () => {
                axios.post(route('loads.load_orders.update_loaded', { load: this.load_id, load_order: loadOrder.id }), { loaded })
                    .then((response) => {
                        if (response.data.load_order) {
                            loadOrder.loaded_at = response.data.load_order.loaded_at;
                        }
                    })
                    .catch(() => {
                        event.target.checked = !loaded;
                        this.updatePickingLoadingData();
                    });
            });
        },

        // Compare two stops in route visit order (ascending index = earlier on the route).
        compareStopsByVisitOrder (a, b) {
            if (this.load_type === 'delivery') {
                const d = a.delivery_order_index - b.delivery_order_index;
                if (d !== 0) {
                    return d;
                }
                return a.id - b.id;
            }
            const l = a.load_order_index - b.load_order_index;
            if (l !== 0) {
                return l;
            }
            return a.id - b.id;
        },

        // Last visit first so that stop is loaded first and sits deepest in the truck; picking/loading follow the same sequence.
        sortStopsByVisit (stops) {
            return [...stops].sort((a, b) => -this.compareStopsByVisitOrder(a, b));
        },

        isVisitLoaded (lo) {
            if (lo.order_id == null) {
                return !!lo.loaded_at;
            }
            if (!lo.order) {
                return false;
            }
            return !!(lo.order.loaded_at) || Number(lo.order.status) === 2;
        },
        
        // Tell the callback to happen in 500 ms
        saveOrderDebounce: function (type, load_order_id, callback = null)
        {
            if (this['saveOrder' + type + 'Timeout'][load_order_id] != undefined) clearTimeout(this['saveOrder' + type + 'Timeout'][load_order_id]);
            
            if (typeof callback == 'function')
            {
                this['saveOrder' + type + 'Timeout'][load_order_id] = setTimeout(() => {
                    callback();
                    clearTimeout(this['saveOrder' + type + 'Timeout'][load_order_id]);
                }, 250);
            }
        },

        updatePickingLoadingData () {
            axios.get(route('loads.load_order_picking_loading_updates', {load: this.load_id}))
            .then((response) => {
                this.loadOrdersData = response.data.load_orders
            })
        },
        
        printLabels(order) {
            if (this.shouldAutoprint('labels')) {
                this.autoprintWithPrinterPrompt(route('orders.print'), { orders: order.id, printType: 'labels' })
                    .finally(() => {
                        setTimeout(()=> {
                            this.$inertia.reload({ only: ['load'] });
                        }, 250);
                    });
            } else {
                window.open((route('orders.print', { orders: order.id, printType: 'labels'} )), "_blank");
                setTimeout(()=> {
                    this.$inertia.reload({ only: ['load'] });
                }, 250);
            }
        },

        subtotal (order) {
            return computeOrderTotals(order).subtotal;
        },

        /** Merchandise subtotal before promo discounts are applied. */
        subtotalBeforeDiscount (order) {
            return computeOrderTotals(order).subtotalBeforeDiscount;
        },

        applied_credit (order) {
            return parseFloat(order.applied_credit_amount ?? 0);
        },

        total (order) {
            return computeOrderTotals(order).total;
        },

        tax (order) {
            return computeOrderTotals(order).tax;
        },

        delivery_fee (order) {
            return computeOrderTotals(order).deliveryFeeAfterDiscount;
        },

        getBackground (loadOrder, index) {
            if (loadOrder.order_id != null && loadOrder.order.picking_checked_at == null && loadOrder.order.packaged_at == null && loadOrder.order.loaded_at == null)
            {
                let orderItems = loadOrder.order.order_items;
                let orderItemCount = orderItems.length;
                let pickedOrderItemCount = orderItems.filter(oi => oi.picked_at !== null).length;
                let pickedPrc = Math.round(pickedOrderItemCount / orderItemCount * 10000) / 100;

                if (pickedPrc == 0)
                    return null;
                else if (index % 2 == 0)
                    return 'linear-gradient(90deg, rgba(252, 218, 218, 1)' + pickedPrc + '%, transparent ' + (pickedPrc + 2) + '%)';
                else if (index % 2 == 1)
                    return 'linear-gradient(90deg, rgba(252, 218, 218, 1)' + pickedPrc + '%, rgba(249, 249, 249, 1) ' + (pickedPrc + 2) + '%)';
                else return 'rgba(133, 199, 30, 0.2)';
            }

            return null;
        },
    },

    computed: {
        loadableLoadOrderIds () {
            let firstRetailLoadOrderId = null;
            let firstSchoolLoadOrderId = null;
            let firstWholesaleLoadOrderId = null;
            this.loadOrdersData.forEach(load_order => {
                if (load_order.order && !load_order.order.loaded)
                {
                    if (load_order.order.is_wholesale && load_order.order.customer.type === 'School' && firstSchoolLoadOrderId === null) firstSchoolLoadOrderId = load_order.id;
                    else if (load_order.order.is_wholesale && load_order.order.customer.type !== 'School' && firstWholesaleLoadOrderId === null) firstWholesaleLoadOrderId = load_order.id;
                    else if (!load_order.order.is_wholesale && firstRetailLoadOrderId === null) firstRetailLoadOrderId = load_order.id;
                    if (firstRetailLoadOrderId && firstSchoolLoadOrderId && firstWholesaleLoadOrderId) return false;
                }
            });

            return [
                firstRetailLoadOrderId,
                firstSchoolLoadOrderId,
                firstWholesaleLoadOrderId,
            ];
        },

        nextBlockingLoadOrderId () {
            const sorted = this.sortStopsByVisit(this.loadOrdersData);
            for (const lo of sorted) {
                if (!this.isVisitLoaded(lo)) {
                    return lo.id;
                }
            }
            return null;
        },

        nonOrderDescriptionColspan () {
            const base = this.load_type === 'delivery' ? 11 : 10;
            if (this.loadShippedAndInvoiced && !this.isOnlyPicker) {
                return base + 1;
            }
            return base;
        },

        filteredLoadOrders() {
            const nonOrderStops = this.loadOrdersData.filter(lo => lo.order_id == null);

            if (this.orderType == 'retail') {
                const orderStops = this.loadOrdersData.filter(lo => lo.order && lo.order.is_wholesale == 0);
                return this.sortStopsByVisit([...nonOrderStops, ...orderStops]);
            }
            if (this.orderType == 'wholesale') {
                const orderStops = this.loadOrdersData.filter(lo => lo.order && lo.order.is_wholesale == 1 && lo.order.customer?.type !== 'School');
                return this.sortStopsByVisit([...nonOrderStops, ...orderStops]);
            }
            if (this.orderType == 'school') {
                const orderStops = this.loadOrdersData.filter(lo => lo.order && lo.order.is_wholesale == 1 && lo.order.customer?.type === 'School');
                return this.sortStopsByVisit([...nonOrderStops, ...orderStops]);
            }

            return this.sortStopsByVisit(this.loadOrdersData);
        }
    }
}
</script>

<style scoped>
    .order_picked {
        @apply bg-red-500 bg-opacity-20 !important;
    }
    .order_picking_checked {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
    .order_packaged{
        @apply bg-blue-500 bg-opacity-20 !important;
    }
    .order_loaded {
        @apply bg-green bg-opacity-20 !important;
    }
</style>